<template>
  <div class="tw-container tw-mx-auto tw-max-w-4xl tw-px-4 tw-pb-20 tw-pt-6 md:tw-pt-12">
    <div class="tw-flex tw-flex-col tw-gap-6">
      <form class="card info-form tw-px-8 tw-py-6" @submit.prevent="saveSettings">
        <h2 class="tw-mb-4 tw-text-lg">
          <strong>{{ $t('Account') }}</strong>
        </h2>

        <label class="tw-flex tw-items-center tw-justify-between">
          <span class="tw-text-riser-secondaryText">{{ $t('Email') }}</span>

          <input
            v-model="email"
            type="email"
            required
            @change="success = false"
            class="normalize-style tw-rounded-lg tw-border tw-border-black/50 tw-bg-white tw-px-4 tw-py-2 tw-leading-none"
          />
        </label>

        <label class="tw-flex tw-items-center tw-justify-between">
          <span class="tw-text-riser-secondaryText">{{ $t('First Name') }}</span>

          <input
            v-model="firstname"
            type="text"
            required
            @change="success = false"
            class="normalize-style tw-rounded-lg tw-border tw-border-black/50 tw-bg-white tw-px-4 tw-py-2 tw-leading-none"
          />
        </label>

        <label class="tw-flex tw-items-center tw-justify-between">
          <span class="tw-text-riser-secondaryText">{{ $t('Last Name') }}</span>

          <input
            v-model="lastname"
            type="text"
            required
            @change="success = false"
            class="normalize-style tw-rounded-lg tw-border tw-border-black/50 tw-bg-white tw-px-4 tw-py-2 tw-leading-none"
          />
        </label>

        <section class="unit-selection">
          <span class="radio-button-list-title tw-text-riser-secondaryText">{{
            $t('auto-accept-follow-requests')
          }}</span>

          <label class="switch">
            <input v-model="autoAcceptFollowRequests" type="radio" :value="true" />
            <span>{{ $t('Yes') }}</span>
          </label>

          <label class="switch">
            <input v-model="autoAcceptFollowRequests" type="radio" :value="false" />
            <span>{{ $t('No') }}</span>
          </label>
        </section>

        <i18n-t
          v-if="showVerifyMailNote"
          scope="global"
          tag="p"
          keypath="We-sent-a-verification-mail-to-%@"
        >
          <span class="tw-font-bold">{{ latestEmail }}</span>
        </i18n-t>

        <!-- ERROR message -->
        <p v-if="error" class="red">{{ $t(error) }}</p>

        <button
          class="small-button"
          :class="[success ? 'success-button' : 'black-button']"
          type="submit"
          :disabled="loading"
          :value="$t('Save')"
        >
          <font-awesome-icon v-if="loading" class="text-icon" icon="spinner" spin />
          <font-awesome-icon v-if="success" class="text-icon" icon="check" />
          <span>{{ $t('Save') }}</span>
        </button>
      </form>

      <section class="card tw-px-8 tw-py-6">
        <h2 class="tw-mb-4 tw-text-lg">
          <span class="tw-font-bold">{{ $t('Signup-&-Subscription') }}</span>
        </h2>

        <div class="tw-flex tw-items-center tw-justify-between">
          <span class="tw-text-riser-secondaryText">{{ $t('Sign-up-date') }}</span>
          <span class="membership-status">
            <ClientOnly>
              {{ formatDate(new Date(currentUser.created_at)) }}
            </ClientOnly>
          </span>
        </div>

        <div class="tw-flex tw-items-center tw-justify-between">
          <span class="tw-text-riser-secondaryText">{{ $t('Membership') }}</span>
          <span class="membership-status">
            {{ currentUser.account_type }}
          </span>
        </div>

        <template v-if="isPro">
          <div class="tw-flex tw-items-center tw-justify-between">
            <span class="tw-text-riser-secondaryText">{{ $t('Subscribed-until') }}</span>
            <span class="membership-status">
              <ClientOnly>
                {{ formatDate(new Date(currentUser.pro_expiration * 1000)) }}
              </ClientOnly>
            </span>
          </div>
        </template>

        <a
          v-if="billingPortalSession"
          :href="billingPortalSession.url"
          target="_blank"
          class="tw-my-3 tw-inline-block tw-text-riser-blue"
        >
          {{ $t('manage-subscription-and-billing') }}
        </a>
      </section>

      <form class="card tw-px-8 tw-py-6">
        <h2 class="tw-mb-4 tw-text-lg">
          <span class="tw-font-bold">{{ $t('Units') }}</span>
        </h2>

        <section class="unit-selection">
          <span class="radio-button-list-title tw-text-riser-secondaryText">{{
            $t('Distance')
          }}</span>

          <label class="switch">
            <input v-model="distance" type="radio" value="metric" />
            <span>{{ $t('Metric') }}</span>
          </label>

          <label class="switch">
            <input v-model="distance" type="radio" value="imperial" />
            <span>{{ $t('Imperial') }}</span>
          </label>
        </section>

        <section class="unit-selection">
          <span class="radio-button-list-title tw-text-riser-secondaryText">{{
            $t('Temperature')
          }}</span>

          <label class="switch">
            <input v-model="temperature" type="radio" value="celsius" />
            <span>{{ $t('Celsius') }}</span>
          </label>

          <label class="switch">
            <input v-model="temperature" type="radio" value="fahrenheit" />
            <span>{{ $t('Fahrenheit') }}</span>
          </label>
        </section>

        <label class="tw-flex tw-items-center tw-justify-between">
          <span class="tw-text-riser-secondaryText">{{ $t('Start week on') }}</span>

          <InputNativeSelect
            :model-value="weekStartsOn.toString()"
            wrapper-classes="!tw-bg-transparent tw-border tw-rounded-lg tw-border-black/50"
            :options="[
              { label: $t('Monday'), value: 1 },
              { label: $t('Tuesday'), value: 2 },
              { label: $t('Wednesday'), value: 3 },
              { label: $t('Thursday'), value: 4 },
              { label: $t('Friday'), value: 5 },
              { label: $t('Saturday'), value: 6 },
              { label: $t('Sunday'), value: 7 }
            ]"
            @update:model-value="(e) => (weekStartsOn = parseInt(e))"
          />
        </label>
      </form>

      <form class="card tw-px-8 tw-py-6" @submit.prevent="changePassword">
        <h2 class="tw-mb-4 tw-text-lg">
          <strong>{{ $t('Change Password') }}</strong>
        </h2>

        <label class="tw-flex tw-items-center tw-justify-between">
          <span class="tw-text-riser-secondaryText">{{ $t('New Password') }}</span>

          <input
            v-model="newPassword1"
            type="password"
            minlength="8"
            maxlength="32"
            pattern=".{8,32}"
            required
            class="normalize-style tw-rounded-lg tw-border tw-border-black/50 tw-bg-white tw-px-4 tw-py-2 tw-leading-none"
            autocomplete="new-password"
            :title="$t('Passwords have to consist of min 8 and max 32 characters.')"
          />
        </label>

        <label class="tw-flex tw-items-center tw-justify-between">
          <span class="tw-text-riser-secondaryText">{{ $t('Confirm new password') }}</span>

          <input
            v-model="newPassword2"
            type="password"
            minlength="6"
            maxlength="32"
            pattern=".{6,32}"
            required
            class="normalize-style tw-rounded-lg tw-border tw-border-black/50 tw-bg-white tw-px-4 tw-py-2 tw-leading-none"
            autocomplete="new-password"
            :title="$t('Passwords have to consist of min 8 and max 32 characters.')"
          />
        </label>
        <p>{{ $t(passwordFeedback) }}</p>
        <button type="submit" class="black-button small-button">
          <span>{{ $t('Save') }}</span>
        </button>
      </form>

      <section class="card tw-px-8 tw-py-6">
        <h2 class="tw-mb-4 tw-text-lg">
          <strong>{{ $t('Delete Account') }}</strong>
        </h2>

        <p class="tw-text-black/60">
          {{ $t('settings-account-deletion-disclaimer') }}
        </p>

        <InputAppButton
          class="tw-mt-4 tw-inline-block tw-cursor-pointer tw-text-riser-red"
          @click="showDeletePopup = !showDeletePopup"
        >
          <span>{{ $t('Delete Account') }}</span>
        </InputAppButton>
      </section>
    </div>
  </div>

  <!-- Account deletion confirmation popup -->
  <modal v-if="showDeletePopup" @close="showDeletePopup = false">
    <template #header>
      <span>{{ $t('Warning') }}</span>
    </template>

    <template #body>
      <div v-html="$t('settings-account-deletion-disclaimer')" />

      <form class="deletion-form tw-mt-4" @submit.prevent="deleteAccount">
        <!-- Simple safety check -->
        <label class="deletion-label">
          <i18n-t scope="global" keypath="Please-type-in-%@-to-confirm"><b>RISER</b></i18n-t>

          <input
            v-model="deleteInput"
            type="text"
            class="tw-mt-2 tw-inline-block tw-rounded-lg tw-bg-white tw-py-1"
            required
          />
        </label>
        <br />

        <button
          class="tw-mt-6 tw-w-full tw-rounded-lg tw-border-none tw-bg-riser-red tw-px-4 tw-py-2 tw-text-center tw-text-white"
          type="submit"
          :disabled="!deleteButtonEnabled"
        >
          {{ $t('Delete Account') }}
        </button>
      </form>
    </template>
  </modal>
</template>

<script>
import Modal from '~/components/modal'

import { formatDate } from '~/utils'

import { ANALYTICS_EVENTS } from '~/types/analyticsEvents'

definePageMeta({
  middleware: ['nav-guard'],
  layout: 'full-width'
})

export default {
  name: 'Settings',
  components: { Modal },
  async setup() {
    const authStore = useAuthStore()
    const { refreshSession } = useAuthenticationService()

    try {
      // Refresh user session to get latest user data - necessary because we also want the latest email address
      const userData = await refreshSession()

      authStore.loginSuccess({ userData })
    } catch (err) {
      console.error(err)
    }
  },
  data() {
    const authStore = useAuthStore(this.$pinia)
    const mainStore = useMainStore(this.$pinia)

    return {
      loading: false,
      error: false,
      success: false,
      firstname: authStore.session_data.user.firstname,
      lastname: authStore.session_data.user.lastname,
      email: authStore.session_data.user.email,
      address: authStore.session_data.user.address,
      city: authStore.session_data.user.city,
      zip: authStore.session_data.user.zip,
      province: authStore.session_data.user.province,
      counrtry: authStore.session_data.user.country,
      subscriptionStatus: null,
      billingPortalSession: null,
      // Account deletion
      showDeletePopup: false,
      deleteString: 'RISER',
      deleteInput: '',
      // Email verification string
      latestEmail: authStore.currentUser?.email,
      showVerifyMailNote: false,
      // Password changing
      passwordFeedback: '',
      newPassword1: '',
      newPassword2: '',
      // Unit settings
      distance: mainStore?.settings?.distance ?? 'metric',
      temperature: mainStore?.settings?.temperature ?? 'celsius',
      twelveHourClock: mainStore?.settings?.twelveHourClock ?? false,
      weekStartsOn: mainStore?.settings?.weekStartsOn,
      autoAcceptFollowRequests: null
    }
  },
  computed: {
    // Account deletion
    deleteButtonEnabled() {
      return this.deleteInput.toUpperCase() === this.deleteString
    },
    isPro() {
      const authStore = useAuthStore(this.$pinia)

      return authStore.isUserPro
    },
    hasSubscription() {
      // return this.subscriptionStatus && this.subscriptionStatus.subscription && !this.subscriptionStatus.cancelled
      // TODO change this once we have a subscription endpoint working
      return false
    },
    currentUser() {
      const authStore = useAuthStore(this.$pinia)

      return authStore.currentUser
    }
  },
  watch: {
    // TODO: Once server supports it, move these settings to user object
    distance() {
      this.updateUnitSettings({ distance: this.distance })
    },
    temperature() {
      this.updateUnitSettings({ temperature: this.temperature })
    },
    twelveHourClock() {
      this.updateUnitSettings({ twelveHourClock: this.twelveHourClock })
    },
    weekStartsOn() {
      this.updateUnitSettings({ weekStartsOn: this.weekStartsOn })
    }
  },
  created() {
    const mainStore = useMainStore(this.$pinia)

    // Initialize unit settings if undefined
    if (!import.meta.env.SSR && !mainStore.settings) {
      this.distance = mainStore.settings.distance
      this.temperature = mainStore.settings.temperature
      this.twelveHourClock = mainStore.settings.twelveHourClock
    }
  },
  mounted() {
    const { createStripeBillingPortalSession } = useSubscriptionService()

    useLatestUserDataOnce(async (userData) => {
      this.autoAcceptFollowRequests = userData.auto_accept_follow_requests

      if (!userData.stripe_customer_id) return

      const session = await createStripeBillingPortalSession()

      if (!session) return

      this.billingPortalSession = session
    })
  },
  methods: {
    formatDate(date) {
      return formatDate(date)
    },

    async saveSettings() {
      const authStore = useAuthStore(this.$pinia)
      const { verifyUniqueEmail } = useAuthenticationService()
      const { updateUser, setUser } = useUserService()

      if (!authStore.currentUser) {
        return
      }

      this.loading = true
      this.error = false
      this.success = false

      // If user changes email address, check if it's still available
      if (this.email !== authStore.currentUser?.email) {
        try {
          await verifyUniqueEmail(this.email)
        } catch (err) {
          if (err.response && err.response.status === 422) {
            this.error = this.$t('The email address you entered already exists')
            this.loading = false
            return
          }
        }
      }

      const data = {
        firstname: this.firstname,
        lastname: this.lastname,
        email: this.email,
        address: this.address,
        city: this.city,
        zip: this.zip,
        province: this.province,
        country: this.country,
        auto_accept_follow_requests: this.autoAcceptFollowRequests
      }

      try {
        const userData = await authStore.awaitAndSetLatestUserData(
          updateUser(authStore.currentUser.id, data)
        )

        this.success = true

        setUser(userData)

        if (this.email !== this.latestEmail) {
          this.latestEmail = this.email
          this.showVerifyMailNote = true
        }
      } catch {
        this.loading = false
        this.error = this.$t('An unknown error occured. We are sorry for the inconvenience.')
        return
      } finally {
        this.loading = false
      }
    },
    // Account deletion
    async deleteAccount() {
      const { trackEvent } = useAnalyticsTrackingService()
      const { deleteUser } = useUserService()

      const authStore = useAuthStore(this.$pinia)

      if (!authStore.currentUser) {
        return
      }

      deleteUser(authStore.currentUser.id)
      trackEvent(ANALYTICS_EVENTS.accountDelete())
      // TODO-SOON: Some sort of success feedback
      this.$router.push('/logout')
    },
    // Password changing
    async changePassword() {
      const authStore = useAuthStore(this.$pinia)
      const { updateUser } = useUserService()

      if (this.newPassword1 !== this.newPassword2) {
        this.passwordFeedback = this.$t("The passwords you entered don't match.")
        return
      }
      await updateUser(authStore.currentUser.id, {
        password: this.newPassword1
      })
      this.newPassword1 = this.newPassword2 = ''
    },

    updateUnitSettings(newSettingsObject) {
      const mainStore = useMainStore(this.$pinia)

      mainStore.updateSettings(newSettingsObject)
    }
  }
}
</script>

<style lang="sass" scoped>
@import "@/assets/vars.sass"

.info-form input, .info-form textarea
  flex-grow: 1
  max-width: 75%
  resize: none

form, section
  >strong
    text-transform: uppercase

label, .label
  font-weight: normal

label, [type=submit], .membership-status
  margin: $padding 0

.narrowContent>*:not(:last-child)
  margin-bottom: $defaultMargin

.membership-status
  text-transform: uppercase

.delete-button
  text-transform: uppercase
.deletion-label
  font-weight: normal

[disabled]
  cursor: not-allowed
  opacity: .5

.text-icon
  margin-right: 10px

.unit-selection
  display: flex
  align-items: center

.radio-button-list-title
  flex: 2

.switch
  flex: 1
  border: solid 1px rgb(var(--main-text-color))
  cursor: pointer
  overflow: hidden
  &:first-of-type
    border-radius: $border-radius 0 0 $border-radius
  &:last-of-type
    border-radius: 0 $border-radius $border-radius 0
  input
    display: none
  span
    display: block
    padding: 0 $padding
    text-align: center
  *:checked + span
    background-color: rgb(var(--main-text-color))
    color: rgb(var(--secondary-background-color))
</style>
